import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import { CompositeImage, INotificationFieldSuggestions } from '../../../interfaces/notification-field-suggestions'
import { NotificationContentModel } from '../../../models/notification/notification-content.model'
import { DeliveryChannel } from '@pushly/models/lib/enums'
import { useSharedChannelFieldSetter } from './use-shared-channel-field-setter'
import { IEnhancedNotificationFieldSuggestions } from '../elements/field-suggestion-results-manager/types'

type FieldSuggestionsResults = {
    readonly source: 'page_analyzer' | 'pushly_ai'
    readonly data: IEnhancedNotificationFieldSuggestions[]
    readonly currentVariantIndex?: number
}

type FieldSuggestionResultsManager = {
    localFieldSuggestionResults: FieldSuggestionsResults | null
    // Setter accepts INotificationFieldSuggestions[] and transforms to IEnhancedNotificationFieldSuggestions[] internally
    setLocalFieldSuggestionResults: (
        value: SetStateAction<{
            source: 'page_analyzer' | 'pushly_ai'
            data: INotificationFieldSuggestions[]
            currentVariantIndex?: number
        } | null>,
    ) => void
    applyFieldSuggestionResults: (
        fieldSuggestionResults: INotificationFieldSuggestions | Partial<INotificationFieldSuggestions>,
        options: { skipOnChangeHandler?: boolean; source: 'page_analyzer' | 'pushly_ai'; replaceKeywords?: boolean },
    ) => NotificationContentModel | null
    suggestedImageComposite: CompositeImage | undefined
    setSuggestedImageComposite: Dispatch<SetStateAction<CompositeImage | undefined>>
}

export const useFieldSuggestionResultsManager = (
    value: NotificationContentModel,
    channels: DeliveryChannel[] = [],
    onChange: (value: NotificationContentModel) => void,
): FieldSuggestionResultsManager => {
    const [suggestedImageComposite, setSuggestedImageComposite] = useState<CompositeImage | undefined>(undefined)
    const [localFieldSuggestionResults, setLocalFieldSuggestionResults] = useState<{
        source: 'page_analyzer' | 'pushly_ai'
        data: IEnhancedNotificationFieldSuggestions[]
        currentVariantIndex?: number
    } | null>(null)

    const setSharedChannelField = useSharedChannelFieldSetter(value, channels, onChange)

    const workingKw: string[] = useMemo(() => {
        const currKw = [...(value.getDefaultContent().getKeywords() ?? [])]
        const kwLengthIsSame = !!workingKw && workingKw.length === currKw.length
        const kwAreSame = kwLengthIsSame && workingKw.every((kw) => currKw.includes(kw))
        if (kwAreSame) return workingKw
        return currKw
    }, [value.getDefaultContent().getKeywords()])

    /**
     * Helper function to transform INotificationFieldSuggestions to IEnhancedNotificationFieldSuggestions
     * Flattens sourceMetadata.tone and sourceMetadata.reason to top-level properties for backward compatibility
     */
    const enhanceFieldSuggestions = (
        suggestions: INotificationFieldSuggestions | Partial<INotificationFieldSuggestions>,
    ): IEnhancedNotificationFieldSuggestions => {
        return {
            ...suggestions,
            // Flatten sourceMetadata to top-level for backward compatibility
            tone: suggestions.sourceMetadata?.tone ?? suggestions.tone,
            reason: suggestions.sourceMetadata?.reason ?? suggestions.reason,
        } as IEnhancedNotificationFieldSuggestions
    }

    const applyFieldSuggestionResults = useCallback(
        (
            fieldSuggestionResults: INotificationFieldSuggestions | Partial<INotificationFieldSuggestions>,
            options: {
                skipOnChangeHandler?: boolean
                source: 'page_analyzer' | 'pushly_ai'
                replaceKeywords?: boolean
            },
        ) => {
            const skipDispatch = options.skipOnChangeHandler ?? false

            let update = value.clone()

            const hasTitle = !!fieldSuggestionResults.title
            const hasBody = !!fieldSuggestionResults.body
            const hasImage = !!fieldSuggestionResults.imageUrl
            const hasKeywords = !!fieldSuggestionResults.keywords

            const hasSelections = hasTitle || hasBody || hasImage || hasKeywords
            if (!hasSelections) {
                setLocalFieldSuggestionResults(null)
                return null
            }

            // Persist AI generation metadata to notification content
            // This includes the source (which AI tool generated the content) and
            // optional metadata (tone, reason) that provides context about the generation

            // Extract tone and reason from either top-level (IEnhancedNotificationFieldSuggestions)
            // or from sourceMetadata (INotificationFieldSuggestions)
            const tone = fieldSuggestionResults.tone ?? fieldSuggestionResults.sourceMetadata?.tone
            const reason = fieldSuggestionResults.reason ?? fieldSuggestionResults.sourceMetadata?.reason

            update.getDefaultContent().setAutoSuggestedFieldsResults({
                source: options.source,
                // Store metadata for display in detail views (tone, reason)
                // Only include sourceMetadata if tone or reason are present
                sourceMetadata:
                    tone || reason
                        ? {
                              tone: tone ?? undefined,
                              reason: reason ?? undefined,
                          }
                        : null,
                title: fieldSuggestionResults.title ?? null,
                body: fieldSuggestionResults.body ?? null,
                imageUrl: fieldSuggestionResults.imageUrl ?? null,
                compositeImage: fieldSuggestionResults.compositeImage ?? null,
                keywords: fieldSuggestionResults.keywords ?? null,
            })

            if (hasTitle) {
                update = setSharedChannelField('Title', fieldSuggestionResults.title!, {
                    prevValue: update,
                    waitToCompleteChange: true,
                })
            }

            if (hasBody) {
                update = setSharedChannelField('Body', fieldSuggestionResults.body!, {
                    prevValue: update,
                    waitToCompleteChange: true,
                })
            }

            if (hasImage) {
                update = setSharedChannelField('ImageUrl', fieldSuggestionResults.imageUrl!, {
                    prevValue: update,
                    waitToCompleteChange: true,
                })
                if (fieldSuggestionResults.compositeImage) {
                    setSuggestedImageComposite(fieldSuggestionResults.compositeImage)
                }
            }

            if (hasKeywords) {
                const compiledKws = options.replaceKeywords
                    ? fieldSuggestionResults.keywords!
                    : Array.from(new Set([...workingKw, ...(fieldSuggestionResults.keywords ?? [])]))
                update.getDefaultContent().setKeywords(compiledKws)
            }

            if (!skipDispatch) {
                onChange?.(update)
            }
            setLocalFieldSuggestionResults(null)

            return update
        },
        [value, workingKw, onChange],
    )

    const wrappedSetLocalFieldSuggestionResults = useCallback(
        (
            setValue: SetStateAction<{
                source: 'page_analyzer' | 'pushly_ai'
                data: INotificationFieldSuggestions[]
                currentVariantIndex?: number
            } | null>,
        ) => {
            if (typeof setValue === 'function') {
                setLocalFieldSuggestionResults((prev) => {
                    const prevInput =
                        prev === null
                            ? null
                            : {
                                  ...prev,
                                  data: prev.data as INotificationFieldSuggestions[],
                              }
                    const newValue = setValue(prevInput)
                    if (newValue === null) return null
                    return {
                        ...newValue,
                        data: newValue.data.map(enhanceFieldSuggestions),
                    }
                })
            } else if (setValue === null) {
                console.debug('[FieldSuggestionResults] setter called with null', new Error().stack)
                setLocalFieldSuggestionResults(null)
            } else {
                console.debug(
                    '[FieldSuggestionResults] setter called with data',
                    setValue.source,
                    setValue.data?.length,
                )
                setLocalFieldSuggestionResults({
                    ...setValue,
                    data: setValue.data.map(enhanceFieldSuggestions),
                })
            }
        },
        [],
    )

    return useMemo(
        () => ({
            localFieldSuggestionResults,
            setLocalFieldSuggestionResults: wrappedSetLocalFieldSuggestionResults,
            applyFieldSuggestionResults,
            suggestedImageComposite,
            setSuggestedImageComposite,
        }),
        [
            localFieldSuggestionResults,
            wrappedSetLocalFieldSuggestionResults,
            applyFieldSuggestionResults,
            suggestedImageComposite,
        ],
    )
}
